import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { TodoSectionResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useToast } from '@/components/ui/use-toast';

interface CreateSectionInput {
  name: string;
  description?: string;
  parentSectionId?: string;
  sectionType?: 'timeframe' | 'category';
}

interface UpdateSectionInput {
  name?: string;
  description?: string;
  sortOrder?: number;
  sectionType?: 'timeframe' | 'category';
  parentSectionId?: string | null;
  hiddenFromPublic?: boolean;
}

interface UseTodoSectionsReturn {
  createSection: (input: CreateSectionInput) => Promise<TodoSectionResponse>;
  updateSection: (vars: {
    sectionId: string;
    input: UpdateSectionInput;
  }) => Promise<TodoSectionResponse>;
  deleteSection: (sectionId: string) => Promise<void>;
  reorderSections: (sectionIds: string[]) => Promise<void>;
}

export function useTodoSections(listId: string): UseTodoSectionsReturn {
  const queryClient = useQueryClient();
  const detailKey = queryKeys.todoLists.detail(listId);
  const { toast } = useToast();

  const onError = () => {
    toast({ title: 'Something went wrong', variant: 'destructive' });
  };

  const createMut = useMutation({
    mutationFn: (input: CreateSectionInput) => api.createTodoSection(listId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
    },
    onError,
  });

  const updateMut = useMutation({
    mutationFn: ({ sectionId, input }: { sectionId: string; input: UpdateSectionInput }) =>
      api.updateTodoSection(listId, sectionId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
    },
    onError,
  });

  const deleteMut = useMutation({
    mutationFn: (sectionId: string) => api.deleteTodoSection(listId, sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
    },
    onError,
  });

  const reorderMut = useMutation({
    mutationFn: (sectionIds: string[]) => api.reorderTodoSections(listId, sectionIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
    },
    onError,
  });

  return {
    createSection: (input) => createMut.mutateAsync(input),
    updateSection: (vars) => updateMut.mutateAsync(vars),
    deleteSection: (sectionId) => deleteMut.mutateAsync(sectionId),
    reorderSections: (sectionIds) => reorderMut.mutateAsync(sectionIds),
  };
}
